import React from 'react';
import PropTypes from 'prop-types';
import { Box, styled } from '@mui/material';

/**
 * SpeechListeningAnimation Component
 * 
 * Displays an animated sound wave when the app is listening for speech input.
 * The animation consists of multiple bars that animate to simulate sound waves.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.active - Whether the animation is active
 * @param {string} props.size - Size of the animation ('small', 'medium', 'large')
 * @param {string} props.color - Color of the animation bars
 * @param {Object} props.style - Additional styles for the container
 * @returns {React.ReactElement} Speech listening animation component
 */
const SpeechListeningAnimation = ({ active, size = 'medium', color = '#475969', style = {} }) => {
  // Determine size properties based on the size prop
  const getSizeProperties = () => {
    switch (size) {
      case 'small':
        return {
          width: 30,
          height: 30,
          barWidth: 2,
          barGap: 2,
          barCount: 5
        };
      case 'large':
        return {
          width: 100,
          height: 100,
          barWidth: 4,
          barGap: 4,
          barCount: 9
        };
      case 'medium':
      default:
        return {
          width: 60,
          height: 60,
          barWidth: 3,
          barGap: 3,
          barCount: 7
        };
    }
  };

  const { width, height, barWidth, barGap, barCount } = getSizeProperties();

  // Generate the bars for the animation
  const renderBars = () => {
    const bars = [];
    const totalBarWidth = barCount * barWidth + (barCount - 1) * barGap;
    const startX = (width - totalBarWidth) / 2;

    for (let i = 0; i < barCount; i++) {
      // Make sure bars vary more in height for better visualization
      const barHeight = 15 + Math.floor(Math.random() * 40); // Random height between 15-55%
      const xPos = startX + i * (barWidth + barGap);
      const animationDelay = `${i * 0.1}s`;
      const randomDuration = (0.6 + Math.random() * 0.6).toFixed(1); // Duration between 0.6-1.2s

      bars.push(
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${xPos}px`,
            bottom: '25%',
            width: `${barWidth}px`,
            height: `${barHeight}%`,
            backgroundColor: color,
            borderRadius: '2px',
            animation: active ? `soundWave ${randomDuration}s infinite ease-in-out` : 'none',
            animationDelay,
            opacity: active ? 1 : 0.3,
            transition: 'opacity 0.3s, height 0.3s',
            boxShadow: active ? `0 0 8px ${color}80` : 'none',
            transform: 'scale(1)',
            '&:hover': {
              transform: 'scaleY(1.2)'
            }
          }}
        />
      );
    }

    return bars;
  };

  return (
    <Box
      className="speech-animation-container"
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${width}px`,
        height: `${height}px`,
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1200,
        opacity: active ? 1 : 0,
        visibility: active ? 'visible' : 'hidden',
        transition: 'opacity 0.3s, visibility 0.3s',
        '@keyframes soundWave': {
          '0%': { height: '15%', backgroundColor: color },
          '25%': { height: '45%', backgroundColor: color },
          '50%': { height: '65%', backgroundColor: color },
          '75%': { height: '45%', backgroundColor: color },
          '100%': { height: '15%', backgroundColor: color }
        },
        ...style
      }}
    >
      {renderBars()}
    </Box>
  );
};

SpeechListeningAnimation.propTypes = {
  active: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  style: PropTypes.object
};

export default SpeechListeningAnimation;